/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { useSsoErrorReporter } from './useSsoErrorReporter';
import { SCREEN_NAME } from '../config/analytics';
import { Alert, Platform } from 'react-native';
import { NotificationViewStyle } from '@airasia-phoenix/react-native/components/NotificationView';
import type { TFunction } from 'i18next';
import { useSignUp, useUpdateUser } from './account';
import { usePhoneVerificationChannels } from './otp/api';
import {
  filterPhoneVerificationChannels,
  type PhoneVerificationChannel,
} from '../managers/sso';
import { describeSsoError } from '../utils/ssoErrors';
import { formatMobilePhone, normalizeDialingCode } from '../utils/phoneUtils';
import { buildSignUpProfileUpdateBody } from '../utils/signUpFormUtils';
import {
  formatSignUpDateOfBirth,
  type SignUpFormFieldErrors,
  type SignUpFormFieldValues,
  validateSignUpFormFields,
} from '../utils/signUpFormUtils';

export type SignUpFormNotification = {
  text: string;
  style: NotificationViewStyle;
};

export type NavigateToVerifyOtpPayload = {
  phoneNumber: string;
  dialingCode: string;
  availableChannels: PhoneVerificationChannel[];
};

type UseSignUpFormSubmitParams = {
  t: TFunction;
  email?: string;
  password?: string;
  isIncompleteSignup?: boolean;
  onSignUpSuccess?: () => void;
  onSignUpError?: (error: Error) => void;
  onNavigateToVerifyOtp?: (data: NavigateToVerifyOtpPayload) => void;
  setNotification: (notification: SignUpFormNotification | null) => void;
  applyFieldErrors: (errors: SignUpFormFieldErrors) => void;
};

/**
 * Submit orchestration for SignUpFormView — incomplete (passwordless) and
 * classic password signup paths. Keeps API callbacks and navigation identical.
 */
export function useSignUpFormSubmit({
  t,
  email,
  password,
  isIncompleteSignup,
  onSignUpSuccess,
  onSignUpError,
  onNavigateToVerifyOtp,
  setNotification,
  applyFieldErrors,
}: UseSignUpFormSubmitParams) {
  const { isPending: isSigningUp, mutate: signUpMutate } = useSignUp();
  const { isPending: isUpdatingUser, mutate: updateUserMutate } =
    useUpdateUser('SignUpFormView');
  const {
    isPending: isCheckingVerification,
    mutate: checkVerificationMutateV2,
  } = usePhoneVerificationChannels('SignUpFormView');

  const isApiLoading = isSigningUp || isUpdatingUser || isCheckingVerification;
  const reportError = useSsoErrorReporter(SCREEN_NAME.userDetailsV2);

  const showSignupError = useCallback(
    (error: unknown, notifyErrorCallback?: boolean) => {
      const { message: errorMessage } = describeSsoError(
        error,
        t,
        t('signUpFormView.signupFailed'),
      );
      reportError(error, errorMessage);
      setNotification({
        text: errorMessage,
        style: NotificationViewStyle.ERROR,
      });
      if (notifyErrorCallback) {
        onSignUpError?.(error as Error);
      }
    },
    [onSignUpError, reportError, setNotification, t],
  );

  const updateProfileAndFinish = useCallback(
    (profile: {
      firstName: string;
      lastName: string;
      gender: string;
      dateOfBirth: string;
    }) => {
      updateUserMutate(buildSignUpProfileUpdateBody(profile), {
        onSuccess: () => {
          onSignUpSuccess?.();
        },
        onError: (error: unknown) => showSignupError(error, true),
      });
    },
    [onSignUpSuccess, showSignupError, updateUserMutate],
  );

  const submitIncompleteSignup = useCallback(
    (values: SignUpFormFieldValues) => {
      const formattedDateOfBirth = formatSignUpDateOfBirth(values.dob);
      const profile = {
        firstName: values.givenName,
        lastName: values.surName,
        gender: values.selectedGender,
        dateOfBirth: formattedDateOfBirth,
      };

      if (values.dialingCode && values.mobileNumber && email) {
        const formattedMobilePhone = formatMobilePhone(
          values.dialingCode,
          values.mobileNumber,
        );

        checkVerificationMutateV2(
          { mobilePhone: formattedMobilePhone },
          {
            onSuccess: (data) => {
              if (data.isPhoneVerificationRequired) {
                // Spec: empty channel list falls back to SMS and continues to OTP.
                const availableChannels = filterPhoneVerificationChannels(
                  data.availableChannels,
                );
                const channelsForOtp: PhoneVerificationChannel[] =
                  availableChannels.length > 0 ? availableChannels : ['sms'];

                // Persist the profile first; the OTP screen sends the code.
                updateUserMutate(buildSignUpProfileUpdateBody(profile), {
                  onSuccess: () => {
                    onNavigateToVerifyOtp?.({
                      phoneNumber: values.mobileNumber,
                      dialingCode: normalizeDialingCode(values.dialingCode),
                      availableChannels: channelsForOtp,
                    });
                  },
                  onError: (error: unknown) => showSignupError(error, true),
                });
                return;
              }

              updateProfileAndFinish(profile);
            },
            onError: (error: unknown) => showSignupError(error),
          },
        );
        return;
      }

      updateProfileAndFinish(profile);
    },
    [
      checkVerificationMutateV2,
      email,
      onNavigateToVerifyOtp,
      showSignupError,
      updateProfileAndFinish,
      updateUserMutate,
    ],
  );

  const submitPasswordSignup = useCallback(
    (values: SignUpFormFieldValues) => {
      if (!email?.trim() || !password?.trim()) {
        throw new Error('Email or password is missing in SignUp flow');
      }

      const formattedDateOfBirth = formatSignUpDateOfBirth(values.dob);
      const formattedMobilePhone = formatMobilePhone(
        values.dialingCode,
        values.mobileNumber,
      );

      signUpMutate(
        {
          username: email,
          password,
          dateOfBirth: formattedDateOfBirth,
          firstName: values.givenName,
          lastName: values.surName,
          gender: values.selectedGender,
          dialingCode: normalizeDialingCode(values.dialingCode),
          mobilePhone: formattedMobilePhone,
          sendEmail: true,
        },
        {
          onSuccess: () => {
            setNotification({
              text: t('signUpFormView.accountCreatedSuccess'),
              style: NotificationViewStyle.SUCCESS,
            });
            if (Platform.OS === 'web') {
              window.alert(
                `${t('signUpFormView.successAlertTitle')}\n\n${t('signUpFormView.successAlertMessage')}`,
              );
              onSignUpSuccess?.();
            } else {
              Alert.alert(
                t('signUpFormView.successAlertTitle'),
                t('signUpFormView.successAlertMessage'),
                [
                  {
                    text: t('signUpFormView.okButton'),
                    onPress: () => onSignUpSuccess?.(),
                  },
                ],
              );
            }
          },
          onError: (error: unknown) => showSignupError(error, true),
        },
      );
    },
    [
      email,
      onSignUpSuccess,
      password,
      setNotification,
      showSignupError,
      signUpMutate,
      t,
    ],
  );

  const handleSubmit = useCallback(
    (values: SignUpFormFieldValues) => {
      const { isValid, errors } = validateSignUpFormFields(values, t);
      applyFieldErrors(errors);
      if (!isValid) return;

      if (isIncompleteSignup) {
        submitIncompleteSignup(values);
      } else {
        //classic — typed email + password
        submitPasswordSignup(values);
      }
    },
    [
      applyFieldErrors,
      isIncompleteSignup,
      submitIncompleteSignup,
      submitPasswordSignup,
      t,
    ],
  );

  return {
    handleSubmit,
    isApiLoading,
  };
}
